import React from "react";
import { ClientFactory } from "@/main/common/ClientFactory";
import type { PublishShortPostRequest, PublishShortPostResponse } from "@/generated-client";
import { MetaHandler } from "@/main/common/MetaHandler";
import {
    getViewerActionRecord,
    rememberViewerActionRecord,
    type ShortPostInlineReplyRecord,
} from "@/main/common/ViewerActionRecords";
import type { Localizer } from "@/main/react/Types";
import { ShortPostForm, type ShortPostAfterPublish } from "@/main/react/shortpost/ShortPostForm";

type TiptapNode = { type?: string; text?: string; content?: TiptapNode[] };

/**
 * The submitted body as plain text, for the inline render under the card: every text node, block nodes
 * joined by a newline. Media and embeds are not drawn inline; the "View" link opens the full post.
 */
export function shortPostSourceJsonToPlainText(sourceJson: string): string {
    let root: TiptapNode;
    try {
        root = JSON.parse(sourceJson) as TiptapNode;
    } catch {
        return "";
    }
    const blocks: string[] = [];
    const inline = (node: TiptapNode): string => {
        if (typeof node.text === "string") {
            return node.text;
        }
        if (node.type === "hardBreak") {
            return "\n";
        }
        return (node.content ?? []).map(inline).join("");
    };
    const walk = (node: TiptapNode) => {
        const children = node.content ?? [];
        if (children.length > 0 && children.every((child) => typeof child.text === "string" || child.type === "hardBreak")) {
            blocks.push(inline(node));
            return;
        }
        children.forEach(walk);
    };
    walk(root);
    return blocks.map((block) => block.trim()).filter(Boolean).join("\n");
}

/**
 * The inline reply box under a short post (round 31 D10): one field, one tap. It must never read as
 * "publish a new post", so its words are the reply's own: the placeholder reuses
 * `thought.editor.placeholder.reply` ("Write your reply…") and the button reuses `typescript.thought.reply`
 * ("Reply"), never the composer's "Post". The placeholder names no member (round 32 D10): a textarea
 * placeholder is a string attribute that cannot carry `translate="no"`, so a name there is the one
 * interpolation the member-name MT guard could never protect. It sends through `POST /ajax/discussion/{discussionPublicId}/reply`, which links the new post
 * `RESPONDS_TO` this one (D6) and notifies this post's author (D9).
 *
 * <p><b>A reply stays in the feed (SV-141 D3).</b> No navigation: the box clears and the reply renders
 * directly under it, from the submitted text and the viewer's `loggedInUsername` meta (A2, no response
 * shape change), with a "View" link (`literal.view`) to the new post's `redirectTo`. It is banked in
 * `ViewerActionRecords` (`short-post-inline-replies`, keyed by THIS post's publicId) because the card is
 * drawn from a list DTO that a Back-restored feed serves stale (rule 33); the box seeds from the record
 * on mount. One entry per reply `publicId`, so an idempotent replay (`created: false`) renders once.
 *
 * <p><b>D3 is the FEED CARD's rule, not the post page's</b> (ruled 2026-09-25). The focal view on a
 * short post's own page (`FocalPostNavigator`) passes `afterReply="navigate"`: the member lands on the new
 * reply, whose page names the post it answers, and no inline list is drawn there. The default stays
 * `"stay"` so the feed card needs no prop. Record: `designs/08-audits-and-process/2026-09-25-devo-gate-triage-5de0e21004.md`.
 */
export function ShortPostReplyBox(props: {
    discussionPublicId: string;
    localizer: Localizer;
    /** `"stay"` (default): the feed card, D3. `"navigate"`: the post page's focal view. */
    afterReply?: "stay" | "navigate";
}) {
    const { discussionPublicId, localizer } = props;
    const stays = (props.afterReply ?? "stay") === "stay";
    const submit = React.useCallback(
        (request: PublishShortPostRequest) =>
            ClientFactory.getAjaxDiscussionRestControllerApi().replyWithShortPost(discussionPublicId, request),
        [discussionPublicId],
    );

    const [replies, setReplies] = React.useState<readonly ShortPostInlineReplyRecord[]>(
        () => getViewerActionRecord("short-post-inline-replies", discussionPublicId) ?? [],
    );
    // A card re-used for another post re-seeds from THAT post's record.
    const [seededFor, setSeededFor] = React.useState(discussionPublicId);
    if (seededFor !== discussionPublicId) {
        setSeededFor(discussionPublicId);
        setReplies(getViewerActionRecord("short-post-inline-replies", discussionPublicId) ?? []);
    }

    const repliesRef = React.useRef(replies);
    repliesRef.current = replies;

    const handlePublished = React.useCallback(
        ({ response, sourceJson }: { response: PublishShortPostResponse; sourceJson: string }) => {
            const { publicId, redirectTo } = response;
            // An idempotent replay of a reply already drawn adds nothing and changes no identity.
            if (!publicId || !redirectTo || repliesRef.current.some((reply) => reply.publicId === publicId)) {
                return;
            }
            const next: readonly ShortPostInlineReplyRecord[] = [
                ...repliesRef.current,
                {
                    publicId,
                    redirectTo,
                    text: shortPostSourceJsonToPlainText(sourceJson),
                    authorUsername: MetaHandler.getLoggedInUsername() ?? "",
                },
            ];
            rememberViewerActionRecord("short-post-inline-replies", discussionPublicId, next);
            repliesRef.current = next;
            setReplies(next);
        },
        [discussionPublicId],
    );

    const afterPublish: ShortPostAfterPublish = React.useMemo(
        () => (stays
            ? { mode: "stay", onPublished: handlePublished }
            : { mode: "navigate", navigationSource: "short_post_reply" }),
        [stays, handlePublished],
    );

    return (
        <>
            <ShortPostForm
                localizer={localizer}
                placeholder={localizer("thought.editor.placeholder.reply")}
                submitLabel={localizer("typescript.thought.reply")}
                submit={submit}
                afterPublish={afterPublish}
                playwrightId="short-post-reply-box"
                draftKey={`short-post-reply-box:${discussionPublicId}`}
                className="mt-2"
            />
            {stays && replies.length > 0 ? (
                <ul className="mt-2 flex flex-col gap-2" data-playwright-id="short-post-inline-replies">
                    {replies.map((reply) => (
                        <li
                            key={reply.publicId}
                            className="rounded-md border border-border px-3 py-2 text-sm"
                            data-playwright-id="short-post-inline-reply"
                        >
                            <div className="flex items-center justify-between gap-2">
                                {reply.authorUsername ? (
                                    <span className="font-medium text-foreground" translate="no">
                                        {reply.authorUsername}
                                    </span>
                                ) : <span />}
                                <a
                                    href={reply.redirectTo}
                                    className="shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
                                    data-playwright-id="short-post-inline-reply-view"
                                >
                                    {localizer("literal.view")}
                                </a>
                            </div>
                            <p className="mt-1 whitespace-pre-line break-words text-foreground">{reply.text}</p>
                        </li>
                    ))}
                </ul>
            ) : null}
        </>
    );
}
